import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from "react";
import { CROP_RATIOS, HANDLES, WHOLE, fitted, moveFrame, resizeFrame, rounded, shareAspect, } from "./crop.js";
import { BUTTON_CLASS, FIELD_CLASS, HINT_CLASS, LABEL_CLASS, PRIMARY_CLASS } from "./styles.js";
const RATIO_WORDS = {
    free: "Frei",
    "16:9": "16:9 (breit)",
    "4:3": "4:3",
    "1:1": "1:1 (quadratisch)",
};
/** Where each grip sits on the frame, and the cursor it shows. */
const GRIP_PLACES = {
    nw: "left-0 top-0 cursor-nwse-resize",
    n: "left-1/2 top-0 cursor-ns-resize",
    ne: "left-full top-0 cursor-nesw-resize",
    e: "left-full top-1/2 cursor-ew-resize",
    se: "left-full top-full cursor-nwse-resize",
    s: "left-1/2 top-full cursor-ns-resize",
    sw: "left-0 top-full cursor-nesw-resize",
    w: "left-0 top-1/2 cursor-ew-resize",
};
/** One arrow press: a hundredth of the picture. */
const STEP = 0.01;
const ARROWS = {
    ArrowLeft: [-STEP, 0],
    ArrowRight: [STEP, 0],
    ArrowUp: [0, -STEP],
    ArrowDown: [0, STEP],
};
/**
 * Cutting a stored picture (D44), like the crop of a word processor: the original with a
 * frame, eight grips, a proportion to keep, and the frame the picture had last. The cut itself
 * is the server's; the dialog only says which part.
 *
 * A native `<dialog>` opened modal: the browser keeps the focus inside
 * and gives `Esc`. The frame takes the keyboard: arrows move it, with Shift they move its
 * lower right corner. The grips are for the pointer only.
 */
export function CropDialog({ src, alt, initial, ratio: initialRatio, busy, onApply, onCancel, }) {
    const ref = useRef(null);
    const picture = useRef(null);
    const handle = useRef(null);
    const drag = useRef(null);
    const id = useId();
    const [ratio, setRatio] = useState(initial === null ? initialRatio : "free");
    // The picture's own proportion, known once it has loaded; until then no proportion holds.
    const [pictureAspect, setPictureAspect] = useState(0);
    const [frame, setFrame] = useState(initial ?? WHOLE);
    const aspect = shareAspect(ratio, pictureAspect);
    useEffect(() => {
        const dialog = ref.current;
        if (dialog === null) {
            return undefined;
        }
        if (typeof dialog.showModal === "function") {
            dialog.showModal();
        }
        else {
            dialog.setAttribute("open", "");
        }
        handle.current?.focus();
        return () => {
            if (typeof dialog.close === "function" && dialog.open) {
                dialog.close();
            }
        };
    }, []);
    function loaded() {
        const image = picture.current;
        if (image === null || image.naturalHeight === 0) {
            return;
        }
        const measured = image.naturalWidth / image.naturalHeight;
        setPictureAspect(measured);
        if (initial === null) {
            setFrame(fitted(shareAspect(ratio, measured)));
        }
    }
    function begin(grip, event) {
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { grip, startX: event.clientX, startY: event.clientY, frame };
    }
    function follow(event) {
        const current = drag.current;
        const box = picture.current?.getBoundingClientRect();
        if (current === null || box === undefined || box.width === 0) {
            return;
        }
        const dx = (event.clientX - current.startX) / box.width;
        const dy = (event.clientY - current.startY) / box.height;
        setFrame(current.grip === "move"
            ? moveFrame(current.frame, dx, dy)
            : resizeFrame(current.frame, current.grip, dx, dy, aspect));
    }
    function end() {
        drag.current = null;
    }
    function keyed(event) {
        const move = ARROWS[event.key];
        if (move === undefined) {
            return;
        }
        event.preventDefault();
        const [dx, dy] = move;
        setFrame((current) => event.shiftKey ? resizeFrame(current, "se", dx, dy, aspect) : moveFrame(current, dx, dy));
    }
    const percent = (share) => `${String(share * 100)}%`;
    const placed = {
        left: percent(frame.x),
        top: percent(frame.y),
        width: percent(frame.w),
        height: percent(frame.h),
    };
    return (_jsxs("dialog", { ref: ref, "aria-labelledby": `${id}-titel`, className: "m-auto w-[min(48rem,calc(100vw-2rem))] border border-line border-t-4 border-t-brand bg-surface p-5 text-ink backdrop:bg-ink/40", onCancel: (event) => {
            event.preventDefault();
            onCancel();
        }, children: [_jsx("h2", { id: `${id}-titel`, className: "font-sans text-base font-semibold", children: "Bild zuschneiden" }), _jsxs("div", { className: "sm:max-w-xs", children: [_jsx("label", { className: LABEL_CLASS, htmlFor: `${id}-format`, children: "Seitenverh\u00E4ltnis" }), _jsx("select", { id: `${id}-format`, className: FIELD_CLASS, value: ratio, onChange: (event) => {
                            const next = event.target.value;
                            setRatio(next);
                            // "Frei" only lifts the lock: the frame stays where it was.
                            if (next !== "free") {
                                setFrame(fitted(shareAspect(next, pictureAspect)));
                            }
                        }, children: CROP_RATIOS.map((value) => (_jsx("option", { value: value, children: RATIO_WORDS[value] }, value))) })] }), _jsx("div", { className: "mt-4 flex justify-center bg-surface-sunken p-2", children: _jsxs("div", { className: "relative inline-block touch-none select-none", children: [_jsx("img", { ref: picture, src: src, alt: alt, className: "block max-h-[60vh] max-w-full", draggable: false, onLoad: loaded }), _jsx("div", { "aria-hidden": "true", className: "pointer-events-none absolute inset-0 overflow-hidden", children: _jsx("div", { className: "absolute shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]", style: placed }) }), _jsx("button", { ref: handle, type: "button", "aria-label": "Ausschnitt", "aria-describedby": `${id}-tasten`, className: "absolute cursor-move rounded-none border-2 border-dashed border-white bg-transparent p-0 outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand", style: placed, onPointerDown: (event) => {
                                begin("move", event);
                            }, onPointerMove: follow, onPointerUp: end, onPointerCancel: end, onKeyDown: keyed, children: HANDLES.map((grip) => (_jsx("span", { "aria-hidden": "true", 
                                // A 24 px target (WCAG 2.5.8) around a 12 px square that shows it.
                                className: `absolute size-6 -translate-x-1/2 -translate-y-1/2 after:absolute after:inset-1.5 after:border after:border-ink after:bg-white after:content-[''] ${GRIP_PLACES[grip]}`, onPointerDown: (event) => {
                                    begin(grip, event);
                                }, onPointerMove: follow, onPointerUp: end, onPointerCancel: end }, grip))) })] }) }), _jsxs("p", { id: `${id}-tasten`, className: HINT_CLASS, children: ["Rahmen oder Ecken ziehen.", _jsx("span", { className: "sr-only", children: " Pfeiltasten verschieben, mit Umschalt die Gr\u00F6\u00DFe." })] }), _jsxs("div", { className: "mt-4 flex flex-wrap gap-2", children: [_jsx("button", { type: "button", className: PRIMARY_CLASS, disabled: busy, onClick: () => {
                            onApply(rounded(frame));
                        }, children: "\u00DCbernehmen" }), _jsx("button", { type: "button", className: BUTTON_CLASS, disabled: busy, onClick: () => {
                            setRatio("free");
                            setFrame(WHOLE);
                        }, children: "Ganzes Bild" }), _jsx("button", { type: "button", className: BUTTON_CLASS, disabled: busy, onClick: onCancel, children: "Abbrechen" })] })] }));
}
/**
 * The frame cut from a picture the browser holds, for the preview before the save (D88): the
 * server cuts the same shares of the same file when it goes up. `null` where the browser cannot
 * draw it (no canvas, a picture without a size of its own); the preview then shows it whole.
 */
export function cutOut(src, frame) {
    return new Promise((resolve) => {
        const picture = new Image();
        picture.onload = () => {
            const { naturalWidth: width, naturalHeight: height } = picture;
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(width * frame.w));
            canvas.height = Math.max(1, Math.round(height * frame.h));
            const context = width > 0 && height > 0 ? canvas.getContext("2d") : null;
            if (context === null) {
                resolve(null);
                return;
            }
            context.drawImage(picture, width * frame.x, height * frame.y, width * frame.w, height * frame.h, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(resolve);
        };
        picture.onerror = () => {
            resolve(null);
        };
        picture.src = src;
    });
}
//# sourceMappingURL=CropDialog.js.map